/**
 * Shared power distribution list helpers — grouping and table cell display (#996).
 * Mirrors largeUpsListUtils.
 *
 * The section-agnostic core (grouping, reorder, session patch, duplicate naming,
 * validation, draft, legacy synthesis) comes from the shared repeatable-section
 * factory. Power distribution-specific table column display stays here.
 */

import { DEFAULT_POWER_DISTRIBUTION_CONFIGURATION } from '../../types/powerDistribution';
import {
  buildRepeatableNameSubline,
  filterRepeatableLocationTagTableColumns,
  formatRepeatableLocationTagColumnHeader,
  isNativeCompactLayout,
  repeatableNameColumnUsesDoubleLine,
  type RepeatableLocationTagColumnOptions,
} from '../../utils/locationTagListDisplay';
import {
  generateAutoPowerDistributionName,
  isAutoGeneratedPowerDistributionName,
} from '../../utils/powerDistributionAutoName';
import { convertToPlainText } from '../../utils/richContent/convertToPlainText';
import {
  createRepeatableSectionListUtils,
  parseOptionalNumber as parseOptionalNumberCore,
} from '../repeatable/repeatableSectionListUtils';

import type {
  PowerDistributionConfiguration,
  GroupedPowerDistributionConfigurations,
  UpdatePowerDistributionConfigurationInput,
} from '../../types/powerDistribution';

/** Desktop web columns (#996). BUSWAY carries the descriptive copy (tap off box subline). */
export const POWER_DISTRIBUTION_TABLE_DESKTOP_COLUMNS = [
  'NAME',
  'BUSWAY',
  'AMPERAGE',
  'LENGTH',
  'VOLTAGE',
  'ROOM',
] as const;

/**
 * Compact mirrors desktop column order; LENGTH folds away on narrow screens.
 * ROOM (#999 Story 3.4) replaces the VOLTAGE column on compact; voltage stays as the NAME subline.
 * Desktop: NAME | BUSWAY | AMPERAGE | LENGTH | VOLTAGE | ROOM
 * Compact: NAME (+voltage) | BUSWAY (+tap off box) | AMPERAGE | ROOM
 */
export const POWER_DISTRIBUTION_TABLE_COMPACT_COLUMNS = [
  'NAME',
  'BUSWAY',
  'AMPERAGE',
  'ROOM',
] as const;

/** All column keys (superset for typing and legacy imports). */
export const POWER_DISTRIBUTION_TABLE_COLUMNS =
  POWER_DISTRIBUTION_TABLE_DESKTOP_COLUMNS;

export type PowerDistributionTableLayout = 'desktop' | 'compact';

/** Injected only when `showStatusColumn` is set (Devices view). */
export const POWER_DISTRIBUTION_TABLE_STATUS_COLUMN = 'STATUS' as const;

export type PowerDistributionTableColumnKey =
  | (typeof POWER_DISTRIBUTION_TABLE_DESKTOP_COLUMNS)[number]
  | (typeof POWER_DISTRIBUTION_TABLE_COMPACT_COLUMNS)[number]
  | typeof POWER_DISTRIBUTION_TABLE_STATUS_COLUMN;

export const POWER_DISTRIBUTION_TABLE_COLUMN_ALIGN: Record<
  PowerDistributionTableColumnKey,
  'left' | 'center'
> = {
  NAME: 'left',
  STATUS: 'left',
  BUSWAY: 'left',
  AMPERAGE: 'center',
  LENGTH: 'center',
  VOLTAGE: 'center',
  ROOM: 'left',
};

export type PowerDistributionListSection = 'existing' | 'new';

// ---------------------------------------------------------------------------
// Section-agnostic core (delegated to the shared repeatable-section factory)
// ---------------------------------------------------------------------------

const core = createRepeatableSectionListUtils<
  PowerDistributionConfiguration,
  UpdatePowerDistributionConfigurationInput
>({
  defaultConfiguration: DEFAULT_POWER_DISTRIBUTION_CONFIGURATION,
  generateAutoName: generateAutoPowerDistributionName,
  isAutoGeneratedName: isAutoGeneratedPowerDistributionName,
  namePrefix: 'Power Distribution ',
  newLabel: 'New power distribution',
  nameNoun: 'Power Distribution',
  getDisplayNameFallback: unit =>
    unit.powerOverheadBuswayOrUnderFloor?.trim() || undefined,
  legacySourceKeys: [
    'powerUpsOutputVoltage',
    'powerDistributionVoltageToLoad',
    'powerOverheadBuswayOrUnderFloor',
    'powerBuswayAmperage',
    'powerBuswayLengthFt',
    'powerTapOfBoxRequirements',
  ],
  mapLegacyFields: source => ({
    powerUpsOutputVoltage: source.powerUpsOutputVoltage as string | undefined,
    powerDistributionVoltageToLoad: source.powerDistributionVoltageToLoad as
      | string
      | undefined,
    powerOverheadBuswayOrUnderFloor: source.powerOverheadBuswayOrUnderFloor as
      | string
      | undefined,
    powerBuswayAmperage: source.powerBuswayAmperage as string | undefined,
    powerBuswayLengthFt: source.powerBuswayLengthFt as number | undefined,
    powerTapOfBoxRequirements: source.powerTapOfBoxRequirements as
      | string
      | undefined,
  }),
});

export function normalizePowerDistributionStatus(
  status: PowerDistributionConfiguration['status'] | string | undefined,
): PowerDistributionConfiguration['status'] {
  return core.normalizeStatus(status);
}

export function groupPowerDistributionByStatus(
  units: PowerDistributionConfiguration[],
): GroupedPowerDistributionConfigurations {
  return core.groupByStatus(units);
}

export function sortPowerDistributionByDisplayOrder(
  units: PowerDistributionConfiguration[],
): PowerDistributionConfiguration[] {
  return core.sortByDisplayOrder(units);
}

/** Reorder units within EXISTING or NEW section; reassigns global displayOrder. */
export function applyPowerDistributionSectionReorder(
  sortedUnits: PowerDistributionConfiguration[],
  section: PowerDistributionListSection,
  fromIndex: number,
  toIndex: number,
): PowerDistributionConfiguration[] {
  return core.applySectionReorder(sortedUnits, section, fromIndex, toIndex);
}

/** Merge editor save payload onto a session unit without wiping fields with `undefined`. */
export function applyPowerDistributionSessionPatch(
  unit: PowerDistributionConfiguration,
  patch: UpdatePowerDistributionConfigurationInput,
): PowerDistributionConfiguration {
  return core.applySessionPatch(unit, patch);
}

export function powerDistributionIdsEqual(
  left: number | undefined,
  right: number | undefined,
): boolean {
  return core.idsEqual(left, right);
}

export function getPowerDistributionBuswayDisplay(
  unit: PowerDistributionConfiguration,
): string {
  return unit.powerOverheadBuswayOrUnderFloor?.trim() || '—';
}

/** NAME column primary — user-defined name, else "Power Distribution N" by display order. */
export function getPowerDistributionNamePrimaryDisplay(
  unit: PowerDistributionConfiguration,
): string {
  return core.getNamePrimaryDisplay(unit);
}

/** Editor chrome title — user name, else "Power Distribution N", else "New power distribution". */
export function getPowerDistributionEditChromeTitle(
  name: string | undefined,
  options: {
    isNew?: boolean;
    displayOrder?: number;
    positionIndex?: number;
  } = {},
): string {
  return core.getEditChromeTitle(name, options);
}

export function formatPowerDistributionTableCell(
  unit: PowerDistributionConfiguration,
  column: PowerDistributionTableColumnKey,
): string {
  switch (column) {
    case 'NAME':
      return getPowerDistributionNamePrimaryDisplay(unit);
    case 'BUSWAY':
      return getPowerDistributionBuswayDisplay(unit);
    case 'AMPERAGE':
      return unit.powerBuswayAmperage?.trim() || '—';
    case 'LENGTH':
      return unit.powerBuswayLengthFt != null
        ? String(unit.powerBuswayLengthFt)
        : '—';
    case 'VOLTAGE':
      return unit.powerDistributionVoltageToLoad?.trim() || '—';
    case 'ROOM':
      // Rendered via LocationRoomCell in the row; text unused.
      return '—';
    case 'STATUS':
      // Rendered via RepeatableUnitStatusPill in the row.
      return '';
    default:
      return '—';
  }
}

export function powerDistributionTableColumnsForLayout(
  layout: PowerDistributionTableLayout,
  options?: RepeatableLocationTagColumnOptions,
): readonly PowerDistributionTableColumnKey[] {
  const columns =
    layout === 'compact'
      ? POWER_DISTRIBUTION_TABLE_COMPACT_COLUMNS
      : POWER_DISTRIBUTION_TABLE_DESKTOP_COLUMNS;
  return filterRepeatableLocationTagTableColumns(columns, options, layout);
}

export function powerDistributionTableHeaderLabel(
  column: PowerDistributionTableColumnKey,
  layout: PowerDistributionTableLayout,
): string {
  if (layout === 'compact') {
    if (column === 'AMPERAGE') {
      // Compact pins AMPERAGE to a fixed narrow trailing column, so use a short
      // header on the shared compact layout (native + mobile web). Wide desktop
      // web keeps the full 'AMPERAGE' label via the fall-through below (#999).
      return formatRepeatableLocationTagColumnHeader(column, 'AMP');
    }
  }
  return formatRepeatableLocationTagColumnHeader(column, column);
}

/** BUSWAY subline — tap off box requirements when set. */
export function getPowerDistributionTapOffBoxSubline(
  unit: PowerDistributionConfiguration,
): string | undefined {
  const tapOffBox = convertToPlainText(
    unit.powerTapOfBoxRequirements?.trim() ?? '',
  )
    .replace(/\s+/g, ' ')
    .trim();
  return tapOffBox || undefined;
}

/** NAME subline (compact) — UPS output voltage. */
export function getPowerDistributionVoltageSubline(
  unit: PowerDistributionConfiguration,
): string | undefined {
  const voltage = unit.powerUpsOutputVoltage?.trim();
  return voltage || undefined;
}

export function getPowerDistributionColumnSubline(
  column: PowerDistributionTableColumnKey,
  unit: PowerDistributionConfiguration,
  layout: PowerDistributionTableLayout,
  options?: { locationTagName?: string | null },
): string | undefined {
  // Native compact (#999 Option B): STATUS pill folds under NAME, the voltage fold
  // relocates to the trailing AMPERAGE cell (bare — voltage reads as e.g. `480V`),
  // and BUSWAY carries a `Tap:` label.
  if (isNativeCompactLayout(layout)) {
    if (column === 'BUSWAY') {
      const tap = getPowerDistributionTapOffBoxSubline(unit);
      return tap ? `Tap: ${tap}` : undefined;
    }
    if (column === 'AMPERAGE') {
      return getPowerDistributionVoltageSubline(unit);
    }
    return undefined;
  }
  if (column === 'NAME') {
    return buildRepeatableNameSubline(layout, {
      compactParts:
        layout === 'compact'
          ? [getPowerDistributionVoltageSubline(unit)]
          : undefined,
      locationTagName: options?.locationTagName,
    });
  }
  if (column === 'BUSWAY') {
    return getPowerDistributionTapOffBoxSubline(unit);
  }
  return undefined;
}

export function powerDistributionColumnUsesDoubleLine(
  column: PowerDistributionTableColumnKey,
  layout: PowerDistributionTableLayout = 'desktop',
  options?: { locationTagName?: string | null },
): boolean {
  if (isNativeCompactLayout(layout)) {
    // NAME is single-line (pill renders beneath it in the row); BUSWAY (middle)
    // and AMPERAGE (trailing) double-line their relocated sublines.
    return column === 'BUSWAY' || column === 'AMPERAGE';
  }
  if (column === 'NAME') {
    return repeatableNameColumnUsesDoubleLine(layout, options?.locationTagName);
  }
  return column === 'BUSWAY';
}

/**
 * Primary list title — user-defined name preferred, then busway description, then
 * "Power Distribution N" when displayOrder is available. Returns null when no suitable name.
 */
export function getPowerDistributionDisplayName(
  unit: PowerDistributionConfiguration,
): string | null {
  return core.getDisplayName(unit);
}

/** Unique duplicate name — user names get `-copy`; unnamed/auto names get a fresh serial. */
export function buildDuplicatePowerDistributionName(
  sourceName: string | undefined,
  takenNames: Iterable<string | undefined> = [],
  tempId: number,
): string {
  return core.buildDuplicateName(sourceName, takenNames, tempId);
}

/** Assign a serial name when the user did not provide one. */
export function ensurePowerDistributionNameForCreate(
  data: UpdatePowerDistributionConfigurationInput,
  tempId: number,
  takenNames: Iterable<string | undefined> = [],
): UpdatePowerDistributionConfigurationInput {
  return core.ensureNameForCreate(data, tempId, takenNames);
}

/** Client-side uniqueness check before persisting power distribution session changes. */
export function validateSessionPowerDistributionNames(
  session: PowerDistributionConfiguration[],
): string | null {
  return core.validateSessionNames(session);
}

/** Clone fields for duplicate flow (no id/metadata; keeps source existing/new status). */
export function clonePowerDistributionForCreate(
  source: PowerDistributionConfiguration,
  takenNames: Iterable<string | undefined> = [],
  tempId: number,
): Partial<PowerDistributionConfiguration> {
  return core.cloneForCreate(source, takenNames, tempId);
}

/** Parse numeric form input — returns undefined for empty or non-finite values. */
export const parseOptionalNumber = parseOptionalNumberCore;

export function createDraftPowerDistributionConfiguration(
  data: UpdatePowerDistributionConfigurationInput,
  displayOrder: number,
  tempId: number,
): PowerDistributionConfiguration {
  return core.createDraftConfiguration(data, displayOrder, tempId);
}

/** Client-only id for a unit synthesized from legacy flat request columns. */
export const LEGACY_POWER_DISTRIBUTION_SYNTHETIC_ID = core.SYNTHETIC_ID;

/** Whether a list row may offer delete (draft rows or persisted id > 0, not legacy synthetic). */
export function canDeletePowerDistributionFromList(
  unit: PowerDistributionConfiguration,
  isDraftMode: boolean,
): boolean {
  return core.canDeleteFromList(unit, isDraftMode);
}

export function hasLegacyPowerDistributionRequestFields(
  source: Record<string, unknown>,
): boolean {
  return core.hasLegacyRequestFields(source);
}

/** Build one display row from pre–multi-instance flat request fields when child rows are absent. */
export function buildLegacyPowerDistributionFromRequestFields(
  source: Record<string, unknown>,
  requestId: string | number,
): PowerDistributionConfiguration | null {
  return core.buildLegacyFromRequestFields(source, requestId);
}
